import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const MODES = ["Sea Freight", "Air Freight", "Road Transport", "Multimodal"];
const TRADES = ["Export", "Import", "On-Demand"];

const empty = { name: "", email: "", company: "", phone: "", origin: "", destination: "", mode: "Sea Freight", trade: "Export", cargo_type: "", message: "" };

export default function QuoteForm({ open, onClose }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      // 1. Try primary API backend first
      await axios.post(`${API}/quote`, form, { timeout: 2500 });
    } catch (err) {
      // 2. Direct background email submission to info@suryavanshexim.com (no redirects, no mail app popups)
      try {
        await axios.post("https://formsubmit.co/ajax/info@suryavanshexim.com", {
          name: form.name,
          email: form.email,
          company: form.company || "N/A",
          phone: form.phone || "N/A",
          trade_type: form.trade,
          freight_mode: form.mode,
          origin: form.origin || "N/A",
          destination: form.destination || "N/A",
          cargo_type: form.cargo_type || "N/A",
          message: form.message || "N/A",
          _subject: `New Quote Request — ${form.name} (${form.trade})`,
          _captcha: "false",
        });
      } catch (submitErr) {
        console.log("Background email submission error:", submitErr);
      }
    } finally {
      setLoading(false);
      setDone(true);
      toast.success("Quote request submitted! Our trade desk will reply within 24 hours.");
      setTimeout(() => {
        setDone(false);
        setForm(empty);
        onClose();
      }, 2600);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="quote-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="glass relative z-10 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl p-7 sm:p-9"
          >
            <button onClick={onClose} data-testid="quote-close-btn" className="absolute right-5 top-5 text-slate-400 hover:text-white">
              <X className="h-6 w-6" />
            </button>

            {done ? (
              <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="quote-form-success-toast">
                <CheckCircle2 className="h-16 w-16 text-[#D4AF37]" />
                <h3 className="mt-5 font-display text-2xl font-bold text-white">Request Received</h3>
                <p className="mt-2 text-slate-400">Thank you, {form.name.split(" ")[0]}. Our trade desk is on it.</p>
              </div>
            ) : (
              <>
                <span className="data-label">Get Started</span>
                <h3 className="mt-2 font-display text-2xl font-bold uppercase text-white sm:text-3xl">Request a Freight Quote</h3>
                <p className="mt-1 text-sm text-slate-400">Tell us about your shipment. We reply within 24 hours.</p>

                <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Full Name *" testId="quote-form-name-input" value={form.name} onChange={set("name")} placeholder="Your name" />
                  <Field label="Email *" testId="quote-form-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" />
                  <Field label="Company" testId="quote-form-company-input" value={form.company} onChange={set("company")} placeholder="Company name" />
                  <Field label="Phone" testId="quote-form-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
                  <Field label="Origin" testId="quote-form-origin-input" value={form.origin} onChange={set("origin")} placeholder="e.g. Mumbai, India" />
                  <Field label="Destination" testId="quote-form-destination-input" value={form.destination} onChange={set("destination")} placeholder="e.g. Hamburg, Germany" />

                  <Select label="Trade Type" testId="quote-form-trade-select" value={form.trade} onChange={set("trade")} options={TRADES} />
                  <Select label="Freight Mode" testId="quote-form-mode-select" value={form.mode} onChange={set("mode")} options={MODES} />

                  <div className="sm:col-span-2">
                    <Field label="Cargo Type" testId="quote-form-cargo-input" value={form.cargo_type} onChange={set("cargo_type")} placeholder="e.g. Agricultural grains, 12 tonnes" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="data-label mb-1.5 block">Message</label>
                    <textarea
                      data-testid="quote-form-message-input"
                      value={form.message}
                      onChange={set("message")}
                      rows={3}
                      placeholder="Any additional details..."
                      className="w-full rounded-xl border border-white/10 bg-[#0B1320] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#E65100]"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="quote-form-submit-btn"
                    className="group mt-2 flex items-center justify-center gap-2 rounded-full bg-[#E65100] px-7 py-3.5 font-semibold text-white transition-all duration-300 hover:bg-[#FF6D00] hover:shadow-[0_0_30px_rgba(230,81,0,0.5)] disabled:opacity-60 sm:col-span-2"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                    {loading ? "Sending..." : "Submit Request"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <div>
      <label className="data-label mb-1.5 block">{label}</label>
      <input
        data-testid={testId}
        {...props}
        className="w-full rounded-xl border border-white/10 bg-[#0B1320] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#E65100]"
      />
    </div>
  );
}

function Select({ label, testId, options, ...props }) {
  return (
    <div>
      <label className="data-label mb-1.5 block">{label}</label>
      <select
        data-testid={testId}
        {...props}
        className="w-full rounded-xl border border-white/10 bg-[#0B1320] px-4 py-3 text-sm text-white outline-none transition-colors focus:border-[#E65100]"
      >
        {options.map((o) => <option key={o} value={o} className="bg-[#0B1320]">{o}</option>)}
      </select>
    </div>
  );
}
